import { ContestType } from '@betterpool/api-types/contests-service';
import { useMemo } from 'react';

import { getTeamOrPlayerImageUrl } from '~/domains/common/utils/getTeamOrPlayerImageUrl';
import usePlayerContestEntriesWithPicks from '~/domains/contest/hooks/usePlayerContestEntriesWithPicks';

import { useContestContext } from '../../common/context/ContestContext/ContestContext';
import { useSlatesContext } from '../../common/context/SlatesContext/SlatesContext';
import type { GamecenterPlayerEntryProps } from '../components/EntryScheduledSingleSlate/EntryScheduledSingleSlate';
import { getEntryPickState } from '../../common/components/GamecenterEntriesPageScheduled/GamecenterEntry/entryPickState';

function usePlayerEntriesScheduled() {
  const { contest } = useContestContext();
  const { slates, expectedPicksCount } = useSlatesContext([
    ContestType.PLAYER_TIER,
    ContestType.PLAYER_ONE_AND_DONE,
  ]);

  const { data, isLoading, error, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    usePlayerContestEntriesWithPicks({
      contest,
      contestId: contest.id,
    });

  const entriesItems = useMemo(() => {
    if (isLoading || isError || !data) return [];

    return data.pages.flatMap(({ entries, picks }) =>
      entries.map((entry) => {
        const entryPicks = picks.entries[entry.id];

        return {
          id: entry.id,
          name: entry.name,
          entryOrder: entry.entryOrder,
          isMultiOwner: entry.isMultiOwner,
          pickState: getEntryPickState(
            entry.isPickController,
            slates.map((slate) => {
              const slatePicks = entryPicks?.slates[slate.id];
              return {
                picks: slatePicks?.picks ?? [],
                isMissingPicks: slatePicks?.isMissingPicks,
              };
            })
          ),
          slates: slates.map((slate) => {
            const slatePicks = entryPicks?.slates[slate.id];

            return {
              id: slate.id,
              name: slate.slate.name,
              picks:
                slatePicks?.picks.map((pick) => ({
                  id: pick.playerId,
                  name: pick.name,
                  image: getTeamOrPlayerImageUrl(contest.league.alias, pick.playerId),
                  isInjured: !!pick.attributes.injury,
                })) ?? [],
              isMakePicks: slate.slate.isPicksheetAvailable,
              isMissingPicks: slatePicks?.isMissingPicks,
              requiredPicksCount: expectedPicksCount,
            };
          }),
        } satisfies GamecenterPlayerEntryProps;
      })
    );
  }, [contest.league.alias, data, expectedPicksCount, isError, isLoading, slates]);

  return {
    entries: entriesItems,
    fetchNextPage,
    hasNextPage,
    isError,
    error,
    isFetchingNextPage,
    isLoading,
  };
}

export default usePlayerEntriesScheduled;
